<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { lockBodyScroll, unlockBodyScroll } from '@/utils/bodyScrollLock'

const props = withDefaults(defineProps<{
    open: boolean
    label: string
    maxWidth?: string
    panelClass?: string
    mode?: 'adaptive' | 'dialog' | 'sheet'
    teleportTo?: string | HTMLElement
}>(), {
    maxWidth: '42rem',
    panelClass: '',
    mode: 'adaptive',
    teleportTo: 'body',
})

const emit = defineEmits<{
    close: []
    afterLeave: []
}>()

const panel = ref<HTMLElement>()
const desktop = useMediaQuery('(min-width: 768px)')
const transitionDuration = computed(() => props.mode === 'dialog' || (props.mode !== 'sheet' && desktop.value)
    ? { enter: 280, leave: 180 }
    : { enter: 420, leave: 260 })
let locked = false
const scrollLockOwner = Symbol('adaptive-overlay')

function close() {
    emit('close')
}

function lockScroll() {
    if (locked) { return }
    lockBodyScroll(scrollLockOwner)
    window.addEventListener('keydown', onKeydown)
    locked = true
}

function unlockScroll() {
    if (!locked) { return }
    unlockBodyScroll(scrollLockOwner)
    window.removeEventListener('keydown', onKeydown)
    locked = false
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && props.open) { close() }
}

function afterLeave() {
    unlockScroll()
    emit('afterLeave')
}

watch(() => props.open, async (open) => {
    if (open) {
        lockScroll()
        await nextTick()
        if (document.activeElement === document.body) { panel.value?.focus() }
    }
}, { immediate: true })

onBeforeUnmount(() => {
    unlockScroll()
})
</script>

<template>
    <Teleport :to="teleportTo">
        <Transition name="adaptive-overlay" :duration="transitionDuration" @after-leave="afterLeave">
            <div v-if="open" class="adaptive-overlay fixed inset-0 z-50 grid place-items-end md:place-items-center md:p-6" :class="{ 'adaptive-overlay--dialog': mode === 'dialog', 'adaptive-overlay--sheet': mode === 'sheet' }">
                <div class="adaptive-overlay__scrim absolute inset-0 bg-[var(--c-overlay)]" aria-hidden="true" @click="close" />
                <div
                    ref="panel"
                    class="adaptive-overlay__panel relative z-1 max-h-[92dvh] w-full overflow-hidden border border-b-0 border-[var(--c-border)] rounded-t-[38px] bg-[var(--c-surface-raised)] md:max-h-[calc(100dvh-3rem)] md:border-b md:rounded-[30px]"
                    :class="panelClass"
                    :style="{ maxWidth }"
                    role="dialog"
                    aria-modal="true"
                    :aria-label="label"
                    tabindex="-1"
                >
                    <slot />
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.adaptive-overlay__scrim {
    opacity: 1;
    transition: opacity 220ms ease;
}

.adaptive-overlay__panel {
    opacity: 1;
    transform: translate3d(0, 0, 0);
    transition: transform 420ms cubic-bezier(0.32, 0.72, 0, 1);
    backface-visibility: hidden;
    contain: paint;
    transform-origin: center;
}

.adaptive-overlay-enter-active .adaptive-overlay__panel,
.adaptive-overlay-leave-active .adaptive-overlay__panel {
    will-change: transform;
}

.adaptive-overlay-enter-from .adaptive-overlay__scrim,
.adaptive-overlay-leave-to .adaptive-overlay__scrim {
    opacity: 0;
}

.adaptive-overlay-enter-from .adaptive-overlay__panel,
.adaptive-overlay-leave-to .adaptive-overlay__panel {
    transform: translate3d(0, 100%, 0);
}

.adaptive-overlay-leave-active .adaptive-overlay__scrim {
    transition-duration: 180ms;
}

.adaptive-overlay-leave-active .adaptive-overlay__panel {
    transition-duration: 260ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.adaptive-overlay--dialog {
    place-items: center;
    padding: 1rem;
}

.adaptive-overlay--sheet {
    place-items: end center;
    padding: 0;
}

.adaptive-overlay--sheet .adaptive-overlay__panel {
    max-height: 94dvh;
    border-bottom-width: 0;
    border-radius: 24px 24px 0 0;
}

.adaptive-overlay--dialog .adaptive-overlay__panel {
    border-bottom-width: 1px;
    border-radius: 28px;
}

.adaptive-overlay-enter-active.adaptive-overlay--dialog .adaptive-overlay__panel,
.adaptive-overlay-leave-active.adaptive-overlay--dialog .adaptive-overlay__panel {
    transition: none;
}

.adaptive-overlay-enter-active.adaptive-overlay--dialog .adaptive-overlay__panel {
    animation: dialog-in 280ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.adaptive-overlay-leave-active.adaptive-overlay--dialog .adaptive-overlay__panel {
    animation: dialog-out 180ms cubic-bezier(0.4, 0, 0.2, 1) both;
}

@keyframes dialog-in {
    from {
        opacity: 0;
        transform: translate3d(0, 10px, 0) scale(0.985);
    }

    to {
        opacity: 1;
        transform: translate3d(0, 0, 0) scale(1);
    }
}

@keyframes dialog-out {
    from {
        opacity: 1;
        transform: translate3d(0, 0, 0) scale(1);
    }

    to {
        opacity: 0;
        transform: translate3d(0, 5px, 0) scale(0.99);
    }
}

@media (min-width: 768px) {
    .adaptive-overlay__panel {
        contain: paint;
        box-shadow: 0 24px 64px rgb(0 0 0 / 22%);
        transition: none;
    }

    .adaptive-overlay-enter-active .adaptive-overlay__panel {
        animation: dialog-in 280ms cubic-bezier(0.22, 1, 0.36, 1) both;
    }

    .adaptive-overlay-leave-active .adaptive-overlay__panel {
        animation: dialog-out 180ms cubic-bezier(0.4, 0, 0.2, 1) both;
    }

    .adaptive-overlay--sheet .adaptive-overlay__panel {
        contain: paint;
        border-bottom-width: 0;
        border-radius: 24px 24px 0 0;
        box-shadow: 0 -18px 54px rgb(0 0 0 / 28%);
    }

    .adaptive-overlay-enter-active.adaptive-overlay--sheet .adaptive-overlay__panel,
    .adaptive-overlay-leave-active.adaptive-overlay--sheet .adaptive-overlay__panel {
        animation: none;
        transition: transform 420ms cubic-bezier(0.32, 0.72, 0, 1);
    }

    .adaptive-overlay-leave-active.adaptive-overlay--sheet .adaptive-overlay__panel {
        transition-duration: 260ms;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    }
}
</style>
